/**
 * @file     topics.ts
 * @purpose  The topic grammar and change-signal contract shared by the server
 *           emitter and the client subscription primitive.
 */

/**
 * Topic grammar. Each string is authorized by an RLS policy on
 * realtime.messages (migration 20260719170000):
 *
 *   user:<uuid>     buyer's own stream    -> that buyer, plus admin
 *   admin           admin-wide stream     -> admin only
 *   contact:<uuid>  staff view of a buyer -> admin, plus assigned partner
 *
 * Staff and buyer streams are deliberately separate topics for the same person:
 * a buyer must never receive admin-internal signals about themselves.
 */
export const topics = {
  user: (userId: string) => `user:${userId}`,
  admin: () => 'admin',
  contact: (userId: string) => `contact:${userId}`,
} as const;

/**
 * What changed. This is a signal, not a data transport.
 *
 * Payloads carry no row data on purpose. Authorization on a broadcast is
 * topic-level, so anything placed in the payload is visible to every subscriber
 * of that topic, with no column-level control and no way to apply the display
 * rules the REST layer enforces. Instead a subscriber reconciles by refetching
 * through its normal authorized route, which re-checks permissions and returns
 * the shape that surface is allowed to see.
 *
 * The practical benefit: a resource is a coarse bucket, so a new field or a
 * changed DTO never requires a matching change here.
 */
export type LiveResource =
  | 'suggestions'
  | 'messages'
  | 'threads'
  | 'saved-listings'
  | 'saved-searches'
  | 'assignments'
  | 'activity'
  | 'notifications';

export type LiveChangePayload = {
  resource: LiveResource;
  /** Emission time (ISO). Diagnostic only - never used for ordering. */
  at: string;
};

/** The single broadcast event name. Subscribers filter on `resource`. */
export const LIVE_EVENT = 'changed' as const;
